import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Music, Volume2, VolumeX } from "lucide-react";
import { useLang } from "../LanguageContext";
import { waLink } from "../data/site";

/* Musique de méditation douce — nappe chaleureuse (accord Do Maj 9) et bols
   chantants cristallins, synthétisés en direct via Web Audio API, aucun fichier à charger. */
export const AmbientAudio = () => {
  const [on, setOn] = useState(false);
  const [hint, setHint] = useState(false);
  const { s } = useLang();
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const bellRef = useRef(null);

  const build = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = 0;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1300;
    master.connect(filter).connect(ctx.destination);

    // Nappe de méditation : accord doux avec souffle lent et légère largeur
    const pad = [
      { f: 130.81, g: 0.3, lfo: 0.03 },
      { f: 196.0, g: 0.24, lfo: 0.045 },
      { f: 261.63, g: 0.2, lfo: 0.04 },
      { f: 293.66, g: 0.12, lfo: 0.06 },
      { f: 329.63, g: 0.16, lfo: 0.05 },
    ];
    pad.forEach(({ f, g, lfo }) => {
      [-4, 4].forEach((cents) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = f;
        osc.detune.value = cents;
        const gain = ctx.createGain();
        gain.gain.value = g / 2;
        const breath = ctx.createOscillator();
        breath.frequency.value = lfo;
        const breathGain = ctx.createGain();
        breathGain.gain.value = (g / 2) * 0.6;
        breath.connect(breathGain).connect(gain.gain);
        osc.connect(gain).connect(master);
        osc.start();
        breath.start();
      });
    });

    // Souffle d'air très léger, comme une brise entre les nuages
    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      last = (last + (Math.random() * 2 - 1) * 0.04) * 0.98;
      data[i] = last;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.value = 550;
    noiseFilter.Q.value = 0.6;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.5;
    noise.connect(noiseFilter).connect(noiseGain).connect(master);
    noise.start();

    // Bols chantants : notes cristallines espacées, jamais à l'improviste
    const NOTES = [523.25, 587.33, 659.25, 783.99, 1046.5];
    const ring = () => {
      const t = ctx.currentTime;
      const f = NOTES[Math.floor(Math.random() * NOTES.length)];
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.055, t + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 6);
      const harm = ctx.createOscillator();
      harm.type = "sine";
      harm.frequency.value = f * 2.76;
      const harmGain = ctx.createGain();
      harmGain.gain.setValueAtTime(0, t);
      harmGain.gain.linearRampToValueAtTime(0.012, t + 0.06);
      harmGain.gain.exponentialRampToValueAtTime(0.0001, t + 3.5);
      osc.connect(gain).connect(master);
      harm.connect(harmGain).connect(master);
      osc.start(t);
      harm.start(t);
      osc.stop(t + 6.5);
      harm.stop(t + 4);
      bellRef.current = setTimeout(ring, 7000 + Math.random() * 8000);
    };
    bellRef.current = setTimeout(ring, 2500);

    ctxRef.current = ctx;
    masterRef.current = master;
  };

  const start = async () => {
    if (!ctxRef.current) build();
    const ctx = ctxRef.current;
    await ctx.resume();
    const t = ctx.currentTime;
    masterRef.current.gain.cancelScheduledValues(t);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, t);
    masterRef.current.gain.linearRampToValueAtTime(0.055, t + 5); // montée en douceur
    setOn(true);
  };

  const stop = () => {
    if (!ctxRef.current) return;
    clearTimeout(bellRef.current);
    const t = ctxRef.current.currentTime;
    masterRef.current.gain.cancelScheduledValues(t);
    masterRef.current.gain.setValueAtTime(masterRef.current.gain.value, t);
    masterRef.current.gain.linearRampToValueAtTime(0, t + 1.4);
    setOn(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => setHint(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Bulle sonore — apparaît en douceur */}
      <motion.button
        data-testid="audio-toggle-btn"
        onClick={() => (on ? stop() : start())}
        className={`glass fixed bottom-5 left-5 z-50 flex items-center gap-2.5 rounded-full py-3 pl-4 pr-5 text-xs font-medium tracking-wide text-white/85 transition-colors hover:text-gold-light ${!on && hint ? "animate-pulse-soft" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: hint ? 1 : 0.55, y: 0 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        aria-pressed={on}
        title={s.sound.audioTitle}
      >
        {on ? <Volume2 className="h-4 w-4 text-mint" /> : <VolumeX className="h-4 w-4 text-gold" />}
        <span className="hidden sm:inline">{on ? s.sound.audioOn : s.sound.audioOff}</span>
        <Music className="h-3 w-3 text-white/40 sm:hidden" />
      </motion.button>

      {/* Bouton WhatsApp direct flottant */}
      <motion.a
        data-testid="whatsapp-float-btn"
        href={waLink(s.wa.defaultMsg)}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-mint/40 bg-[#0b1512]/80 text-mint shadow-[0_10px_36px_rgba(46,233,166,0.28)] backdrop-blur-md transition-transform duration-300 hover:scale-110"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        aria-label={s.nav.waFloat}
        title={`${s.contact.channels.whatsapp} — +237 678 406 408`}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.81-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29" />
        </svg>
      </motion.a>
    </>
  );
};
